import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { authFetch, API_BASE } from "../auth";
import "./Home.css";
import TickerLabel from "../components/TickerLabel";

const API = API_BASE;

const STRATEGIES = [
  { title: "Buy on Close", desc: "Enter near market close based on your signals.", path: "/buy-on-close", icon: "\u{1F552}" },
  { title: "Straight Purchase", desc: "Simple buy flow with your preferred sizing.", path: "/straight-purchase", icon: "\u{1F6D2}" },
  { title: "Price Based Running Man", desc: "Trigger logic based on price levels and momentum.", path: "/price-based-running-man", icon: "\u{1F4C8}" },
  { title: "Trailing Sell", desc: "Set trailing stop orders on open positions.", path: "/trailing-sell", icon: "\u{1F4C9}" },
  { title: "Analytics", desc: "P&L tracking, trade history, and audit log.", path: "/analytics", icon: "\u{1F4CA}" },
];

/* ── Confirmation Modal ── */
function ConfirmModal({ open, title, message, onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div className="kill-overlay" onClick={onCancel}>
      <div className="kill-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kill-modal__icon">{"\u26A0"}</div>
        <h3 className="kill-modal__title">{title}</h3>
        <p className="kill-modal__message">{message}</p>
        <div className="kill-modal__actions">
          <button className="kill-modal__cancel" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className="kill-modal__confirm" onClick={onConfirm} disabled={loading}>
            {loading ? "Sending\u2026" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

ConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

/* ── Trail Visualization Bar ── */
function TrailBar({ trailStopPrice, auxPrice, currentPrice }) {
  if (!trailStopPrice || !auxPrice || !currentPrice) return null;

  const gap = currentPrice - trailStopPrice;
  const pct = Math.max(0, Math.min(100, (gap / auxPrice) * 100));

  let colorClass = "trail-bar--safe";
  if (pct < 33) colorClass = "trail-bar--danger";
  else if (pct < 66) colorClass = "trail-bar--warning";

  return (
    <div className="trail-vis">
      <div className="trail-vis__labels">
        <span className="trail-vis__stop">Stop ${trailStopPrice.toFixed(2)}</span>
        <span className="trail-vis__gap">
          ${gap.toFixed(2)} cushion &middot; Trail ${auxPrice.toFixed(2)}
        </span>
        <span className="trail-vis__price">Price ${currentPrice.toFixed(2)}</span>
      </div>
      <div className="trail-vis__track">
        <div className={`trail-vis__fill ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

TrailBar.propTypes = {
  trailStopPrice: PropTypes.number,
  auxPrice: PropTypes.number,
  currentPrice: PropTypes.number,
};

export default function Home() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [account, setAccount] = useState({ netLiquidation: null, dailyPnL: null });
  const [collapsed, setCollapsed] = useState({});
  const [selling, setSelling] = useState({});

  // Active orders state
  const [orders, setOrders] = useState([]);
  const [cancelling, setCancelling] = useState({});

  // Confirmation modal state
  const [confirm, setConfirm] = useState({ open: false, title: "", message: "", action: null });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAccount = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/account-summary/`);
      const data = await res.json();
      if (res.ok) setAccount(data);
    } catch (_) { /* silent */ }
  }, []);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`${API}/positions/`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch");
      setPositions(data.positions || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/open-orders/`);
      const data = await res.json();
      if (res.ok) setOrders(data.orders || []);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchPositions();
    fetchAccount();
    fetchOrders();
    const posInterval = setInterval(fetchPositions, 30000);
    const acctInterval = setInterval(fetchAccount, 30000);
    const ordInterval = setInterval(fetchOrders, 10000);
    return () => { clearInterval(posInterval); clearInterval(acctInterval); clearInterval(ordInterval); };
  }, [fetchPositions, fetchAccount, fetchOrders]);

  const toggleTicker = (ticker) => {
    setCollapsed((p) => ({ ...p, [ticker]: !p[ticker] }));
  };

  const handleSell = async (conId, quantity, localSymbol) => {
    if (!window.confirm(`Sell ${quantity} of ${localSymbol}?`)) return;
    setSelling((p) => ({ ...p, [conId]: true }));
    try {
      const res = await authFetch(`${API}/sell-position/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conId, quantity }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Sell failed");
      alert(`Sold! Order ID: ${data.orderId}`);
      fetchPositions();
      fetchOrders();
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setSelling((p) => ({ ...p, [conId]: false }));
    }
  };

  const handleCancelOrder = async (orderId, localSymbol) => {
    if (!window.confirm(`Cancel order for ${localSymbol}?`)) return;
    setCancelling((p) => ({ ...p, [orderId]: true }));
    try {
      const res = await authFetch(`${API}/cancel-order/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Cancel failed");
      alert(data.message || `Order ${orderId} cancelled in IBKR.`);
      fetchOrders();
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setCancelling((p) => ({ ...p, [orderId]: false }));
    }
  };

  const doCancelAll = async () => {
    const res = await authFetch(`${API}/cancel-all-orders/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Cancel all failed");
    alert(data.message || `Cancelled ${data.cancelled} order(s) in IBKR.`);
    fetchOrders();
  };

  // ── Kill / Trail Kill helpers ──

  const askConfirm = (title, message, action) => {
    setConfirm({ open: true, title, message, action });
  };

  const closeConfirm = () => {
    setConfirm({ open: false, title: "", message: "", action: null });
  };

  const runConfirmedAction = async () => {
    if (!confirm.action) return;
    setActionLoading(true);
    try {
      await confirm.action();
      fetchPositions();
      fetchOrders();
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setActionLoading(false);
      closeConfirm();
    }
  };

  const doKill = async (ticker) => {
    const body = ticker ? { ticker } : {};
    const res = await authFetch(`${API}/kill-positions/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Kill failed");
    const placed = (data.results || []).filter((r) => r.status === "placed").length;
    alert(`Kill orders placed: ${placed} position(s)`);
  };

  const doTrailKill = async (ticker) => {
    const body = ticker ? { ticker } : {};
    const res = await authFetch(`${API}/trail-kill-positions/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Trail kill failed");
    const placed = (data.results || []).filter((r) => r.status === "placed").length;
    alert(`Trail kill orders placed: ${placed} position(s)`);
  };

  const tickers = Object.keys(positions).sort();
  const totalPositions = tickers.reduce((s, t) => s + positions[t].length, 0);

  const trailOrders = orders.filter((o) => o.orderType === "TRAIL" || o.orderType === "TRAIL LIMIT");
  const otherOrders = orders.filter((o) => o.orderType !== "TRAIL" && o.orderType !== "TRAIL LIMIT");

  return (
    <div>
      <ConfirmModal
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        onConfirm={runConfirmedAction}
        onCancel={closeConfirm}
        loading={actionLoading}
      />

      <div className="page-header">
        <h2 className="page-header__title">Trading Dashboard</h2>
        <p className="page-header__subtitle">Open positions and strategy controls.</p>
      </div>

      {/* Account Summary */}
      <section className="acct-summary">
        <div className="acct-summary__card">
          <span className="acct-summary__label">Account Balance</span>
          <span className="acct-summary__value">
            {account.netLiquidation != null
              ? `$${account.netLiquidation.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "\u2014"}
          </span>
        </div>
        <div className="acct-summary__card">
          <span className="acct-summary__label">Today&apos;s P&L</span>
          <span className={`acct-summary__value ${account.dailyPnL != null ? (account.dailyPnL >= 0 ? "pos-green" : "pos-red") : ""}`}>
            {account.dailyPnL != null
              ? `${account.dailyPnL >= 0 ? "+" : ""}$${Math.abs(account.dailyPnL).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "\u2014"}
          </span>
        </div>
      </section>

      {/* ── Active Orders ── */}
      {orders.length > 0 && (
        <section className="orders-section">
          <div className="orders-header">
            <h3 className="orders-header__title">
              Active Orders
              <span className="orders-header__count">{orders.length}</span>
            </h3>
            <div className="orders-header__actions">
              <button
                className="pos-kill-btn pos-kill-btn--hard"
                onClick={() =>
                  askConfirm(
                    "Cancel All Orders",
                    `This will cancel all ${orders.length} active sell order(s).`,
                    doCancelAll
                  )
                }
              >
                Cancel All
              </button>
              <button className="pos-refresh-btn" onClick={fetchOrders}>
                {"\u27F3"} Refresh
              </button>
            </div>
          </div>

          {/* Trail orders */}
          {trailOrders.length > 0 && (
            <div className="orders-group">
              {trailOrders.map((o) => (
                <div key={o.orderId} className="order-card">
                  <div className="order-card__top">
                    <div className="order-card__info">
                      <span className="order-card__symbol">{o.localSymbol || o.symbol}</span>
                      <span className={`order-card__badge order-card__badge--${o.orderType.toLowerCase().replace(/\s+/g, "-")}`}>
                        {o.orderType}
                      </span>
                      <span className="order-card__qty">
                        {"\u00D7"}{o.remaining}{o.filled > 0 ? ` (${o.filled} filled)` : ""}
                      </span>
                    </div>
                    <div className="order-card__right">
                      <span className={`order-card__status order-card__status--${o.status.toLowerCase()}`}>
                        {o.status}
                      </span>
                      <button
                        className="order-card__cancel"
                        title="Cancel order"
                        onClick={() => handleCancelOrder(o.orderId, o.localSymbol || o.symbol)}
                        disabled={cancelling[o.orderId]}
                      >
                        {cancelling[o.orderId] ? "\u2026" : "\u2715"}
                      </button>
                    </div>
                  </div>
                  <TrailBar
                    trailStopPrice={o.trailStopPrice}
                    auxPrice={o.auxPrice}
                    currentPrice={o.currentPrice}
                  />
                  {/* Fallback for trail orders missing market data */}
                  {!o.currentPrice && o.trailStopPrice && (
                    <div className="order-card__price-info">
                      <span>Stop: ${o.trailStopPrice.toFixed(2)}</span>
                      {o.auxPrice && <span>Trail: ${o.auxPrice.toFixed(2)}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Non-trail orders */}
          {otherOrders.length > 0 && (
            <div className="orders-group">
              {otherOrders.map((o) => (
                <div key={o.orderId} className="order-card order-card--compact">
                  <div className="order-card__top">
                    <div className="order-card__info">
                      <span className="order-card__symbol">{o.localSymbol || o.symbol}</span>
                      <span className={`order-card__badge order-card__badge--${o.orderType.toLowerCase().replace(/\s+/g, "-")}`}>
                        {o.orderType}
                      </span>
                      <span className="order-card__qty">
                        {"\u00D7"}{o.remaining}{o.filled > 0 ? ` (${o.filled} filled)` : ""}
                      </span>
                    </div>
                    <div className="order-card__right">
                      {o.lmtPrice != null && (
                        <span className="order-card__lmt">Limit ${o.lmtPrice.toFixed(2)}</span>
                      )}
                      {o.currentPrice != null && (
                        <span className="order-card__cur">Price ${o.currentPrice.toFixed(2)}</span>
                      )}
                      <span className={`order-card__status order-card__status--${o.status.toLowerCase()}`}>
                        {o.status}
                      </span>
                      <button
                        className="order-card__cancel"
                        title="Cancel order"
                        onClick={() => handleCancelOrder(o.orderId, o.localSymbol || o.symbol)}
                        disabled={cancelling[o.orderId]}
                      >
                        {cancelling[o.orderId] ? "\u2026" : "\u2715"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Positions Dashboard */}
      <section className="pos-section">
        <div className="pos-header">
          <h3 className="pos-header__title">Open Positions</h3>
          <div className="pos-header__actions">
            {totalPositions > 0 && (
              <>
                <button
                  className="pos-kill-btn pos-kill-btn--trail"
                  onClick={() =>
                    askConfirm(
                      "Trail Kill All Positions",
                      `This will place $0.10 TRAIL SELL orders on all ${totalPositions} position(s) across ${tickers.length} ticker(s). This cannot be undone from this app.`,
                      () => doTrailKill(null)
                    )
                  }
                >
                  Trail Kill All
                </button>
                <button
                  className="pos-kill-btn pos-kill-btn--hard"
                  onClick={() =>
                    askConfirm(
                      "Kill All Positions",
                      `This will place LIMIT SELL orders at current bid for all ${totalPositions} position(s) across ${tickers.length} ticker(s). This cannot be undone from this app.`,
                      () => doKill(null)
                    )
                  }
                >
                  Kill All
                </button>
              </>
            )}
            <button className="pos-refresh-btn" onClick={fetchPositions} disabled={loading}>
              {loading ? "Loading\u2026" : "\u27F3 Refresh"}
            </button>
          </div>
        </div>

        {error && <div className="pos-error">{error}</div>}

        {!loading && tickers.length === 0 && !error && (
          <div className="pos-empty">No open positions found.</div>
        )}

        {tickers.map((ticker) => {
          const items = positions[ticker];
          const isCollapsed = collapsed[ticker];
          const totalPnl = items.reduce((s, p) => s + (p.unrealizedPnL || 0), 0);

          return (
            <div className="pos-group" key={ticker}>
              <div className="pos-group__header-row">
                <button className="pos-group__header" onClick={() => toggleTicker(ticker)}>
                  <span className="pos-group__symbol"><TickerLabel symbol={ticker} /></span>
                  <span className="pos-group__count">{items.length} position{items.length !== 1 ? "s" : ""}</span>
                  <span className={`pos-group__pnl ${totalPnl >= 0 ? "pos-green" : "pos-red"}`}>
                    {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}
                  </span>
                  <span className="pos-group__chevron">{isCollapsed ? "\u25B8" : "\u25BE"}</span>
                </button>
                <div className="pos-group__kill-btns">
                  <button
                    className="pos-kill-btn--sm pos-kill-btn--trail-sm"
                    title={`Trail Kill all ${ticker} legs`}
                    onClick={(e) => {
                      e.stopPropagation();
                      askConfirm(
                        `Trail Kill ${ticker}`,
                        `Place $0.10 TRAIL SELL on all ${items.length} ${ticker} position(s).`,
                        () => doTrailKill(ticker)
                      );
                    }}
                  >
                    Trail Kill
                  </button>
                  <button
                    className="pos-kill-btn--sm pos-kill-btn--hard-sm"
                    title={`Kill all ${ticker} legs`}
                    onClick={(e) => {
                      e.stopPropagation();
                      askConfirm(
                        `Kill ${ticker}`,
                        `Place LIMIT SELL at current bid for all ${items.length} ${ticker} position(s).`,
                        () => doKill(ticker)
                      );
                    }}
                  >
                    Kill
                  </button>
                </div>
              </div>

              {!isCollapsed && (
                <div className="pos-table-wrap">
                  <table className="pos-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Strike</th>
                        <th>Expiry</th>
                        <th>Qty</th>
                        <th>Avg Cost</th>
                        <th>Price</th>
                        <th>P&L ($)</th>
                        <th>P&L (%)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((p) => (
                        <tr key={p.conId} className={p.unrealizedPnL != null ? (p.unrealizedPnL >= 0 ? "pos-row--gain" : "pos-row--loss") : ""}>
                          <td>{p.right || p.secType}</td>
                          <td>{p.strike ? p.strike.toFixed(1) : "\u2014"}</td>
                          <td>{p.expiry || "\u2014"}</td>
                          <td>{p.quantity}</td>
                          <td>{p.avgCost != null ? p.avgCost.toFixed(2) : "\u2014"}</td>
                          <td>{p.currentPrice != null ? p.currentPrice.toFixed(2) : "\u2014"}</td>
                          <td className={p.unrealizedPnL >= 0 ? "pos-green" : "pos-red"}>
                            {p.unrealizedPnL != null ? `${p.unrealizedPnL >= 0 ? "+" : ""}${p.unrealizedPnL.toFixed(2)}` : "\u2014"}
                          </td>
                          <td className={p.unrealizedPnLPct >= 0 ? "pos-green" : "pos-red"}>
                            {p.unrealizedPnLPct != null ? `${p.unrealizedPnLPct >= 0 ? "+" : ""}${p.unrealizedPnLPct.toFixed(1)}%` : "\u2014"}
                          </td>
                          <td>
                            <button
                              className="pos-sell-btn"
                              onClick={() => handleSell(p.conId, p.quantity, p.localSymbol)}
                              disabled={selling[p.conId]}
                            >
                              {selling[p.conId] ? "\u2026" : "Sell"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* Strategy Cards */}
      <section className="home__strategies">
        <h3 className="home__strategies-title">Strategies</h3>
        <div className="home__grid" aria-label="Strategy list">
          {STRATEGIES.map((s) => (
            <button key={s.path} type="button" className="strategyCard" onClick={() => navigate(s.path)}>
              <span className="strategyCard__icon" aria-hidden="true">{s.icon}</span>
              <span className="strategyCard__text">
                <span className="strategyCard__title">{s.title}</span>
                <span className="strategyCard__desc">{s.desc}</span>
              </span>
              <span className="strategyCard__chev" aria-hidden="true">{"\u2192"}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
